/**
 * Switch an awaiting_prescription order to a different treatment.
 *
 * For the "patient changed their mind during the consult" class (the Debra
 * Vopali case, 2026-07-13: ordered NAD nasal, told Hinds she wants the
 * injection). Only parked orders qualify — anything already transmitted to
 * a pharmacy is a pharmacy-side change.
 *
 * The product picker reuses the orderable-medications list from the
 * prescriptions endpoint (same source as CreateRxTaskModal), fetched on
 * open. On success the card shows what happened downstream: submitted to
 * the pharmacy (med already charted — PPI PDF path), a fresh write-Rx task
 * fired, or parked awaiting the new product's intake.
 */

import { useEffect, useMemo, useState } from 'react';

import {
  useOrderSwitchOptions,
  useSwitchOrderProduct,
} from '@/lib/api/clinicalAdminHooks';
import type { PharmacyOrder } from '@/types/clinicalAdmin';

function fmtMoney(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

const OUTCOME_COPY: Record<string, string> = {
  submitted:
    'Order submitted to the pharmacy — the new medication was already on file.',
  task_fired:
    'A write-Rx task for the new medication is now in the prescriber’s queue.',
  parked:
    'Order re-parked. Check the detail below — it may be waiting on the new intake form or the clinician script.',
};

export function SwitchTreatmentModal({
  patientId,
  order,
  onClose,
}: {
  patientId: string;
  order: PharmacyOrder;
  onClose: () => void;
}) {
  const switchOptions = useOrderSwitchOptions(order.id);
  const switchProduct = useSwitchOrderProduct(patientId);
  const isPending = switchProduct.isPending;
  const result = switchProduct.data;

  const options = useMemo(() => {
    const meds = switchOptions.data?.options ?? [];
    const seen = new Set<string>();
    return meds.filter((m) => {
      if (m.product_id === order.product_id) return false;
      if (seen.has(m.product_id)) return false;
      seen.add(m.product_id);
      return true;
    });
  }, [switchOptions.data, order.product_id]);

  const [newProductId, setNewProductId] = useState('');
  const [priceMode, setPriceMode] = useState<'reprice' | 'keep'>('reprice');
  const [waiveIntake, setWaiveIntake] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, isPending]);

  const submit = () => {
    if (!newProductId) return;
    const ok = window.confirm(
      `Switch ${order.order_number || order.id.slice(0, 8)} from ` +
        `${order.product_id} to ${newProductId}?\n\n` +
        (priceMode === 'reprice'
          ? 'Billing: the subscription moves to the new treatment’s plan price and Stripe immediately charges the prorated difference.'
          : 'Billing: UNCHANGED — the patient keeps paying the current price.') +
        (waiveIntake
          ? '\nIntake: waived — the new treatment’s form will NOT be required.'
          : ''),
    );
    if (!ok) return;
    switchProduct.mutate({
      orderId: order.id,
      body: {
        new_product_id: newProductId,
        price_mode: priceMode,
        waive_intake: waiveIntake,
        note: note.trim() || null,
      },
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-24 bg-ink/60 backdrop-blur-sm overflow-y-auto"
      onClick={() => {
        if (!isPending) onClose();
      }}
    >
      <div
        className="bg-white border border-default shadow-xl w-full max-w-content-md flex flex-col gap-16 p-15 my-24"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-12">
          <div className="flex flex-col gap-4">
            <h2 className="type-heading-lg">Switch treatment</h2>
            <p className="type-body-sm text-ink-muted">
              {order.order_number || order.id.slice(0, 8)} · currently{' '}
              <span className="font-mono">{order.product_id}</span>
            </p>
          </div>
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={onClose}
            aria-label="Close"
            disabled={isPending}
          >
            ✕
          </button>
        </div>

        {result ? (
          <div className="flex flex-col gap-12">
            <div className="border border-default bg-sunken p-12 flex flex-col gap-8">
              <span className="type-label-md font-semibold">
                Switched to {result.to_product}
              </span>
              <span className="type-body-sm">
                {OUTCOME_COPY[result.fulfillment?.outcome ?? ''] ??
                  `Outcome: ${result.fulfillment?.outcome ?? 'unknown'}`}
              </span>
              {result.stripe?.repriced ? (
                <span className="type-body-sm">
                  Billing repriced · prorated charge collected:{' '}
                  {fmtMoney(result.stripe.proration_paid_cents)}
                </span>
              ) : (
                <span className="type-body-sm text-ink-muted">
                  Billing unchanged
                  {result.stripe?.skipped
                    ? ` (${result.stripe.skipped})`
                    : ''}
                </span>
              )}
              {result.intake_regated && (
                <span className="type-body-sm text-warning">
                  The new treatment’s intake form is now required — the
                  patient will be prompted on their portal home tab.
                </span>
              )}
            </div>
            <div className="flex justify-end">
              <button type="button" className="btn-primary btn-sm" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <label className="flex flex-col gap-4">
              <span className="type-label-md">New treatment</span>
              {switchOptions.isLoading ? (
                <span className="type-body-sm text-ink-muted">
                  Loading orderable medications…
                </span>
              ) : (
                <select
                  className="input"
                  value={newProductId}
                  onChange={(e) => setNewProductId(e.target.value)}
                  disabled={isPending}
                >
                  <option value="">Select…</option>
                  {options.map((m) => (
                    <option key={m.product_id} value={m.product_id}>
                      {(m.medication_template_name ?? m.product_id) +
                        ` — ${m.partner_label}`}
                    </option>
                  ))}
                </select>
              )}
            </label>

            <fieldset className="flex flex-col gap-6">
              <span className="type-label-md">Billing</span>
              <label className="flex items-center gap-8 type-body-sm">
                <input
                  type="radio"
                  checked={priceMode === 'reprice'}
                  onChange={() => setPriceMode('reprice')}
                  disabled={isPending}
                />
                Move to the new treatment’s plan price (Stripe prorates and
                charges the difference now)
              </label>
              <label className="flex items-center gap-8 type-body-sm">
                <input
                  type="radio"
                  checked={priceMode === 'keep'}
                  onChange={() => setPriceMode('keep')}
                  disabled={isPending}
                />
                Keep current billing (goodwill — patient keeps the old price)
              </label>
            </fieldset>

            <label className="flex items-center gap-8 type-body-sm">
              <input
                type="checkbox"
                checked={waiveIntake}
                onChange={(e) => setWaiveIntake(e.target.checked)}
                disabled={isPending}
              />
              Waive the new treatment’s intake form (e.g. the sync consult
              already covered screening)
            </label>

            <label className="flex flex-col gap-4">
              <span className="type-label-md">Note (audit trail)</span>
              <input
                className="input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Patient chose injection during 7/8 consult"
                disabled={isPending}
              />
            </label>

            {switchProduct.isError && (
              <p className="type-body-sm text-danger">
                {(switchProduct.error as Error)?.message ??
                  'Switch failed — check the order state.'}
              </p>
            )}

            <div className="flex justify-end gap-8">
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={onClose}
                disabled={isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={submit}
                disabled={isPending || !newProductId}
              >
                {isPending ? 'Switching…' : 'Switch treatment'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
